import PropTypes from 'prop-types'
import { PureComponent } from 'react'
import { MultiSelect } from 'react-multi-select-component'
import { Input } from 'semantic-ui-react'

import { Tooltip } from '@repo/ui'
import { cn } from '@repo/utils'

import { ChildrenSchema } from 'utils'

import styles from './index.css'

const getAvatarFromText = (text) => (
  <span className="p-1 flex items-center justify-center size-7">
    <b className="text-xs">
      {[
        ...text
          .trim()
          .replace(/([^a-z0-9]+)/gi, ' ')
          .split(' ')
          .map((word) => word[0]),
        text.trim()[1],
      ]
        .join('')
        .toLocaleUpperCase()
        .slice(0, 2)}
    </b>
  </span>
)

const OptionsSchema = PropTypes.arrayOf(
  PropTypes.shape({
    label: ChildrenSchema.isRequired,
    value: PropTypes.string.isRequired,

    disabled: PropTypes.bool,
    searchable: PropTypes.string,
  }),
)

const ItemRenderer = ({ checked, disabled, onClick, option }) => (
  <div className={cn('flex items-center gap-1', { disabled })}>
    <input
      checked={checked}
      className={cn('shrink', option.icon !== false && 'hidden')}
      disabled={disabled}
      onChange={onClick}
      tabIndex={-1}
      type="checkbox"
    />
    {option.icon !== false && <div className="icon-item">{option.icon || getAvatarFromText(option.label)}</div>}
    <div className="grow overflow-hidden text-sm">
      <div className="truncate">{option.label}</div>
      {!!option.comment && <div className="text-sm truncate opacity-60">{option.comment}</div>}
    </div>
  </div>
)

ItemRenderer.propTypes = {
  checked: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  option: PropTypes.shape({
    comment: ChildrenSchema,
    icon: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
    label: ChildrenSchema.isRequired,
  }).isRequired,

  disabled: PropTypes.bool,
}

export const valueRenderer = (filter, selected, options) => {
  if (selected.length === options.length) {
    return filter.overrideStrings.allItemsAreSelected
  }

  if (selected.length) {
    return selected.map((item) => {
      if (item.icon === false) {
        return item.label
      }
      return (
        <Tooltip
          key={item.label}
          content={
            <div className="overflow-hidden">
              <div className="truncate">{item.label}</div>
              {!!item.comment && <div className="text-sm truncate opacity-60">{item.comment}</div>}
            </div>
          }
        >
          <span className="icon-item">{item.icon || getAvatarFromText(item.label)}</span>
        </Tooltip>
      )
    })
  }
  return null
}

export function filterOptions(options, filter) {
  if (!filter) {
    return options
  }
  const re = new RegExp(filter.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, String.raw`\$&`), 'i')
  return options.filter((opt) =>
    [
      typeof opt.searchable === 'string' && opt.searchable,
      typeof opt.value === 'string' && opt.value,
      typeof opt.label === 'string' && opt.label,
    ]
      .filter(Boolean)
      .join(' ')
      .match(re),
  )
}

class SearchFilter extends PureComponent {
  state = {
    searchFilter: '',
  }

  componentDidMount() {
    this.setState({ searchFilter: this.props.searchText })
  }

  onChange = (name, value) => {
    const filters = [...this.props.filters]
    const filter = filters.find((f) => f.name === name)
    if (filter) {
      filter.value = value
    }
    this.props.onChange({
      filters,
      search: this.state.searchFilter,
    })
  }

  onSearchChange = (_, { value }) => {
    this.setState({ searchFilter: value }, () => {
      const search = value.trim()
      if (search.length === 0 || search.length >= 3) {
        this.props.onChange({
          filters: this.props.filters,
          search: value.trim(),
        })
      }
    })
  }

  renderFilters = () =>
    this.props.filters.map((filter) => (
      <MultiSelect
        key={`${filter.name}${filter.options.length}`}
        className={cn(styles.multiSelect, filter.highlight && styles.selectHighlight)}
        filterOptions={filterOptions}
        hasSelectAll={false}
        ItemRenderer={ItemRenderer}
        labelledBy={filter.name}
        onChange={(v) => this.onChange(filter.name, v)}
        options={filter.options}
        overrideStrings={{
          allItemsAreSelected: 'Todo',
          clearSearch: 'Limpiar',
          search: 'Buscar...',
          selectAll: 'Todo',
          selectSomeItems: '~Selección~',
          ...filter.overrideStrings,
        }}
        value={filter.value}
        valueRenderer={(...args) => valueRenderer(filter, ...args)}
      />
    ))

  renderSearch = () => (
    <Input
      className={cn(this.props.searchHighlight && styles.searchHighlight)}
      icon="search"
      name="search"
      onChange={this.onSearchChange}
      placeholder="Buscar..."
      value={this.state.searchFilter}
    />
  )

  render() {
    const { ariaLabel, className } = this.props

    return (
      <div aria-label={ariaLabel} className={cn(styles.wrapper, className)}>
        <div className={styles.search}>{this.renderSearch()}</div>
        {this.renderFilters()}
      </div>
    )
  }
}

SearchFilter.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      highlight: PropTypes.bool,
      name: PropTypes.string.isRequired,
      options: OptionsSchema.isRequired,
      overrideStrings: PropTypes.shape({
        allItemsAreSelected: ChildrenSchema,
        clearSearch: ChildrenSchema,
        search: ChildrenSchema,
        selectAll: ChildrenSchema,
        selectSomeItems: ChildrenSchema.isRequired,
      }).isRequired,
      value: OptionsSchema.isRequired,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,

  className: PropTypes.string,
  searchHighlight: PropTypes.bool,
  searchText: PropTypes.string,
}

export default SearchFilter
